import React from 'react';
import styled from 'styled-components';
import TutorialNavigationBar from '@/components/navigation_bars/TutorialNavigationBar';
import config from '@/lib/config';
import { colors } from '@/styles/colors';
import Link from 'next/link';
import { LocaleNS } from '@/lib/locale-ns';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';

const BarMitzvahInvitationGuide: React.FC = () => {
    const { t } = useTranslation([LocaleNS.TUTORIALS], { keyPrefix: 'bar-mitzva' });

    return (
        <>
            <TutorialNavigationBar />
            <ArticleWrapper>
                <BackButton href="/tutorials">← {t('back-to-tutorials')}</BackButton>
                <Heading>{t('title')}</Heading>

                <Paragraph>{t('intro')}</Paragraph>

                <Subheading>{t('why-self-design.title')}</Subheading>
                <Paragraph>{t('why-self-design.content')}</Paragraph>

                <Subheading>{t('steps.title')}</Subheading>
                <List>
                    <ListItem>{t('steps.item1')}</ListItem>
                    <ListItem>{t('steps.item2')}</ListItem>
                    <ListItem>{t('steps.item3')}</ListItem>
                    <ListItem>{t('steps.item4')}</ListItem>
                    <ListItem>{t('steps.item5')}</ListItem>
                </List>

                <Subheading>{t('tips.title')}</Subheading>
                <List>
                    <ListItem>{t('tips.item1')}</ListItem>
                    <ListItem>{t('tips.item2')}</ListItem>
                    <ListItem>{t('tips.item3')}</ListItem>
                    <ListItem>{t('tips.item4')}</ListItem>
                </List>

                <Subheading>{t('digital-or-printed.title')}</Subheading>
                <Paragraph>{t('digital-or-printed.content')}</Paragraph>

                <Subheading>{t('cost.title')}</Subheading>
                <Paragraph>{t('cost.content')}</Paragraph>

                <Subheading>{t('conclusion.title')}</Subheading>
                <Paragraph>{t('conclusion.content')}</Paragraph>
                <CTAButton href="/catalog/bar-mitzva">{t('cta')}</CTAButton>
            </ArticleWrapper>
        </>
    );
};

export const getStaticProps = async () => {
    return {
        props: {
            ...(await serverSideTranslations('he', [LocaleNS.COMMON, LocaleNS.NAV_BARS, LocaleNS.TUTORIALS]))
        }
    };
};

export default BarMitzvahInvitationGuide;

const ArticleWrapper = styled.div`
    padding: 2rem 2rem;
    max-width: 960px;
    margin: 2rem auto;
    direction: rtl;
    background-color: #fff8f2;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
    margin-block-start: calc(2rem + ${config.ui.navbarHeight}px);
    margin-block-end: 2rem;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
`;

const Heading = styled.h1`
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 1rem;
    text-align: right;
    color: #1a1a1a;
`;

const Subheading = styled.h2`
    font-size: 1.5rem;
    font-weight: 600;
    margin-top: 2rem;
    margin-bottom: 1rem;
    text-align: right;
    color: #2a2a2a;
`;

const Paragraph = styled.p`
    font-size: 1.125rem;
    line-height: 1.75rem;
    margin-bottom: 0.75rem;
    text-align: right;
    color: #333;
`;

const ListItem = styled.li`
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
    text-align: right;
    position: relative;
    padding-right: 1.5rem;

    &::before {
        content: '—';
        position: absolute;
        right: 0;
        color: ${colors.primary};
    }
`;

const List = styled.ul`
    padding-right: 1.25rem;
    margin-bottom: 1.5rem;
    direction: rtl;
`;

const BackButton = styled(Link)`
    display: inline-block;
    padding: 0.5rem 1rem;
    margin-bottom: 1rem;
    color: ${colors.primary};
    text-decoration: none;
    font-size: 1rem;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    align-self: flex-end;

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }
`;

const CTAButton = styled(Link)`
    display: inline-block;
    padding: 1rem 2rem;
    margin-top: 2rem;
    background-color: ${colors.primary};
    color: white;
    text-decoration: none;
    font-size: 1.25rem;
    border-radius: 8px;
    transition: background-color 0.2s ease;
    text-align: center;

    &:hover {
        background-color: #0056b3;
    }
`;
